import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, Undo2, MailX, Mail, Car, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const KIND_META = {
  messages: { icon: Mail, label: "Message" },
  "test-drives": { icon: Car, label: "Test drive" },
  "trade-ins": { icon: Wrench, label: "Trade-in" },
};

export default function AdminTrash() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [emptyingAll, setEmptyingAll] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/trash")
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Failed to load trash"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const restore = async (row) => {
    try {
      await api.post(`/admin/trash/${row.kind}/${row.id}/restore`);
      toast.success("Restored to inbox");
      load();
    } catch { toast.error("Restore failed"); }
  };

  const permanentDelete = async (row) => {
    try {
      await api.delete(`/admin/trash/${row.kind}/${row.id}`);
      toast.success("Permanently deleted");
      setConfirmId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const emptyAll = async () => {
    if (!emptyingAll) { setEmptyingAll(true); return; }
    try {
      const { data } = await api.delete("/admin/trash");
      toast.success(`Emptied trash · ${data.removed} items removed`);
      setEmptyingAll(false);
      load();
    } catch {
      toast.error("Empty trash failed");
      setEmptyingAll(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const summaryFor = (row) => {
    if (row.kind === "messages") return row.subject || row.message?.slice(0, 60) || "General inquiry";
    if (row.kind === "test-drives") return row.vehicle_label || `Test drive · ${row.preferred_date}`;
    if (row.kind === "trade-ins") return `${row.year} ${row.make} ${row.model}`;
    return "";
  };

  return (
    <div data-testid="page-admin-trash">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Trash</h1>
            <p className="text-white/50 mt-3 text-sm max-w-lg">
              Deleted messages, test-drive requests, and trade-in submissions live here.
              Restore anything by mistake, or empty the whole thing when you're done.
            </p>
          </div>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={emptyAll}
              data-testid="trash-empty-all"
              className={`text-xs ${emptyingAll ? "btn-primary" : "btn-outline"}`}
            >
              <Trash2 size={12} /> {emptyingAll ? "Click again to confirm empty" : "Empty Trash"}
            </button>
          )}
        </div>
      </motion.div>

      <div className="mt-10 space-y-3">
        {loading ? (
          <div className="glass rounded-2xl p-12 text-center text-white/40">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <MailX size={22} className="mx-auto text-white/30" />
            <div className="text-white/60 mt-4">Trash is empty.</div>
          </div>
        ) : (
          rows.map((row) => {
            const meta = KIND_META[row.kind] || {};
            const Icon = meta.icon || Trash2;
            const confirming = confirmId === row.id;
            return (
              <div
                key={row.id}
                data-testid={`trash-row-${row.id}`}
                className="glass rounded-2xl px-6 py-5 flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={14} className="text-white/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-display text-2xl tracking-tight">{row.full_name}</div>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">{meta.label}</span>
                    <div className="text-xs text-white/40">Deleted {formatDate(row.deleted_at)}</div>
                  </div>
                  <div className="mt-1 text-sm text-white/70">{summaryFor(row)}</div>
                  <div className="mt-1 text-xs text-white/40 truncate">{row.email}{row.phone ? ` · ${row.phone}` : ""}</div>
                </div>

                <div className="flex flex-wrap gap-2 items-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => restore(row)}
                    data-testid={`trash-restore-${row.id}`}
                    className="btn-outline text-xs"
                  >
                    <Undo2 size={12} /> Restore
                  </button>
                  {confirming ? (
                    <>
                      <button
                        type="button"
                        onClick={() => permanentDelete(row)}
                        data-testid={`trash-confirm-delete-${row.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-[var(--primary)] text-white hover:brightness-110"
                      >
                        Delete forever
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/60 hover:text-white"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(row.id)}
                      data-testid={`trash-delete-${row.id}`}
                      className="btn-outline text-xs text-white/60 hover:text-red-400 hover:border-red-500/40"
                    >
                      <Trash2 size={12} /> Delete forever
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
